import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../store/auth.store';
import {
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Avatar
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

export default function UserProfileDropdown() {
  const navigate = useNavigate();

  // 🚀 Map your pre-existing Zustand actions and loading states directly
  const user = useAuthStore((state) => state.user);
  console.log('user', user);
  const logoutAction = useAuthStore((state) => state.logout);
  const purgeAccountAction = useAuthStore((state) => state.purgeAccount);
  const storeLoading = useAuthStore((state) => state.loading);

  // Layout anchor state flags for managing your dropdown menu popover
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  // Separate local state layout flag to control the Delete Account dialog container
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [localError, setLocalError] = useState('');
  // Dropdown UI triggers
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Profile Action Router Links
  const handleGoToProfile = () => {
    handleMenuClose();
    navigate('/management/profile/details');
  };

  const handleGoToChangePassword = () => {
    handleMenuClose();
    navigate('/management/profile/change-password');
  };

  // const handleGoToSettings = () => {
  //   handleMenuClose();
  //   navigate('/settings');
  // };

  const handleStandardLogout = async () => {
    handleMenuClose();
    await logoutAction();
    navigate('/login');
  };

  // Deletion Modal Dialog Pipeline Triggers
  const handleOpenDeleteDialog = () => {
    handleMenuClose(); // 🚀 Closes the menu popover overlay gracefully so it isn't hanging open behind the modal
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (storeLoading) return; // Freeze actions if database isolation script is running
    setDeleteDialogOpen(false);
    setLocalError('');
  };

  const handleExecuteAccountPurge = async () => {
    try {
      setLocalError('');
      // Fires the API mapping transaction you created in the Zustand workspace store
      await purgeAccountAction();
      setDeleteDialogOpen(false);

      // 🚀 REDIRECT ACTION: Push the clean unauthenticated user state back to the home page workspace instantly
      navigate('/', { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        'Failed to complete profile deletion data cleanup transaction.';
      setLocalError(msg);
    }
  };

  return (
    <>
      {/* Target Avatar trigger button for header navigation bars */}
      <IconButton onClick={handleProfileMenuOpen} size="small" sx={{ ml: 2 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          {user?.firstName?.charAt(0) || 'U'}
        </Avatar>
      </IconButton>

      {/* Main Profile Context Dropdown Window Popover */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleGoToProfile}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="My profile" />
        </MenuItem>

        <MenuItem onClick={handleGoToChangePassword}>
          <ListItemIcon>
            <LockOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Change password" />
        </MenuItem>

        {/* <MenuItem onClick={handleGoToSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem> */}

        <MenuItem onClick={handleStandardLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
        {user?.role !== 'admin' && (
          <>
            <Divider />

            <MenuItem
              onClick={handleOpenDeleteDialog}
              sx={{
                color: 'error.main',
                '&:hover': { bgcolor: '#ffebee' } // Smooth custom red warning highlight context
              }}
            >
              <ListItemIcon sx={{ color: 'error.main' }}>
                <DeleteForeverIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Delete my account"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* ⚠️ Purge Confirmation Dialog Module */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="purge-account-dialog-title"
        aria-describedby="purge-account-dialog-description"
      >
        <DialogTitle
          id="purge-account-dialog-title"
          sx={{ fontWeight: 700, pt: 3 }}
        >
          Permanently Delete Your Account?
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            id="purge-account-dialog-description"
            color="text.primary"
            sx={{ mb: 2 }}
          >
            This action is irreversible. All portfolio records, historical trade
            audits, transaction ledgers, and data import logs will be
            permanently liquidated from our databases.
          </DialogContentText>

          {localError && (
            <Typography
              variant="body2"
              color="error"
              fontWeight={600}
              sx={{ mt: 1 }}
            >
              ⚠️ {localError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            disabled={storeLoading}
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            disabled={storeLoading}
            onClick={handleExecuteAccountPurge}
            color="error"
            variant="contained"
            autoFocus
            sx={{ textTransform: 'none', minWidth: 120, fontWeight: 600 }}
          >
            {storeLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Yes, Delete Everything'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
